import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Check, X, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";

const empty = { name: "", slug: "", description: "", image_url: "", visible: true, active: true, sort_order: 0,
                seo_title: "", seo_description: "", og_image: "" };

export default function CollectionsList() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleting, setDeleting] = useState(null);
  const [strategy, setStrategy] = useState("uncategorize");
  const [target, setTarget] = useState("");

  const load = () => api.get("/admin/collections").then((r) => setItems([...r.data].sort((a,b)=>(a.sort_order||0)-(b.sort_order||0))));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing === "new") await api.post("/admin/collections", form);
      else await api.put(`/admin/collections/${editing}`, form);
      setEditing(null); setForm(empty); load(); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (c, field) => {
    await api.put(`/admin/collections/${c.id}`, { ...c, [field]: !c[field] });
    load();
  };

  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await api.post("/admin/collections/reorder", arr.map((x) => x.id));
    load();
  };

  const askDelete = (c) => {
    setDeleting(c);
    setStrategy("uncategorize");
    setTarget(items.find((x) => x.id !== c.id)?.slug || "");
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      const q = strategy === "move" ? `?strategy=move&target=${target}` : `?strategy=${strategy}`;
      const r = await api.delete(`/admin/collections/${deleting.id}${q}`);
      toast.success(`Deleted · ${r.data.products_affected || 0} products reassigned`);
      setDeleting(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <h1 className="font-display text-2xl sm:text-3xl text-gold flex-1">Collections</h1>
        <button data-testid="collection-add" onClick={() => { setEditing("new"); setForm(empty); }} className="btn-gold text-xs"><Plus size={14} className="mr-2" /> Add</button>
      </div>

      {editing && (
        <div className="curio-card p-6 mb-6 space-y-4" data-testid="collection-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="font-caption text-[10px] text-brass block mb-1">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-vintage" data-testid="collection-name" /></div>
            <div><label className="font-caption text-[10px] text-brass block mb-1">URL Slug (auto)</label>
              <input value={form.slug} placeholder="auto-generated" onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input-vintage" /></div>
          </div>
          <div><label className="font-caption text-[10px] text-brass block mb-1">Description</label>
            <textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-vintage" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="font-caption text-[10px] text-brass block mb-1">Image URL</label>
              <input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-vintage" /></div>
            <div><label className="font-caption text-[10px] text-brass block mb-1">Sort Order</label>
              <input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value)||0 })} className="input-vintage" /></div>
          </div>
          <div className="flex gap-6 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible on storefront</label>
            <label className="flex items-center gap-2 text-sm text-parchment/80"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
          </div>
          <details className="border-t border-brass-soft pt-4">
            <summary className="font-caption text-xs text-brass cursor-pointer">SEO / Social</summary>
            <div className="space-y-3 mt-3">
              <div><label className="font-caption text-[10px] text-brass block mb-1">SEO Title</label>
                <input value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="input-vintage" /></div>
              <div><label className="font-caption text-[10px] text-brass block mb-1">Meta Description</label>
                <textarea rows={2} value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="input-vintage" /></div>
              <div><label className="font-caption text-[10px] text-brass block mb-1">Social Share Image URL</label>
                <input value={form.og_image || ""} onChange={(e) => setForm({ ...form, og_image: e.target.value })} className="input-vintage" /></div>
            </div>
          </details>
          <div className="flex gap-3"><button onClick={save} data-testid="collection-save" className="btn-gold text-xs"><Check size={14} className="mr-1" /> Save</button><button onClick={() => setEditing(null)} className="btn-outline text-xs"><X size={14} className="mr-1" /> Cancel</button></div>
        </div>
      )}

      <div className="curio-card divide-y divide-brass-soft/40">
        {items.length === 0 && <div className="p-8 text-center text-parchment/60 italic">No collections yet.</div>}
        {items.map((c, idx) => (
          <div key={c.id} className="flex flex-wrap sm:flex-nowrap items-center p-3 sm:p-4 gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-display text-gold text-base sm:text-lg">{c.name}</div>
              <div className="text-parchment/60 text-xs sm:text-sm italic truncate">/{c.slug} · {c.description || "—"}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} className="text-brass hover:text-gold p-0.5"><ArrowUp size={12} /></button>
                <button onClick={() => move(idx, 1)} className="text-brass hover:text-gold p-0.5"><ArrowDown size={12} /></button>
              </div>
              <button onClick={() => toggle(c, "visible")} title={c.visible ? "Visible" : "Hidden"} className="text-brass hover:text-gold p-1">
                {c.visible ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <span className={`text-[9px] font-caption tracking-wider ${c.active === false ? "text-oxblood" : "text-gold"}`}>{c.active === false ? "INACTIVE" : "ACTIVE"}</span>
              <button onClick={() => { setEditing(c.id); setForm({ ...empty, ...c }); }} data-testid={`collection-edit-${c.slug}`} className="text-brass hover:text-gold p-1"><Edit size={14} /></button>
              <button onClick={() => askDelete(c)} data-testid={`collection-delete-${c.slug}`} className="text-brass hover:text-oxblood p-1"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {deleting && (
        <div className="fixed inset-0 bg-[rgba(9,18,13,0.75)] z-50 flex items-center justify-center p-6" data-testid="collection-delete-modal">
          <div className="curio-card p-6 max-w-md w-full">
            <h2 className="font-display text-xl text-gold mb-3">Delete "{deleting.name}"?</h2>
            <p className="text-parchment/75 text-sm mb-4">Products will <span className="text-gold">not</span> be deleted. Choose what happens to products assigned to this collection:</p>
            <div className="space-y-2 mb-4 text-sm">
              <label className="flex items-center gap-2 text-parchment/85"><input type="radio" checked={strategy === "uncategorize"} onChange={() => setStrategy("uncategorize")} /> Leave products uncategorized</label>
              <label className="flex items-center gap-2 text-parchment/85"><input type="radio" checked={strategy === "move"} onChange={() => setStrategy("move")} /> Move products to another collection</label>
              {strategy === "move" && (
                <select value={target} onChange={(e) => setTarget(e.target.value)} className="input-vintage" data-testid="collection-delete-target">
                  {items.filter((x) => x.id !== deleting.id).map((x) => <option key={x.id} value={x.slug}>{x.name}</option>)}
                </select>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="btn-outline text-xs">Cancel</button>
              <button onClick={confirmDelete} data-testid="collection-delete-confirm" className="btn-gold text-xs">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
